import React, { useCallback, useEffect } from 'react';

import debounce from 'lodash/debounce';
import first from 'lodash/first';

import { padding, useClassnames, classnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { Menu } from '@lumapps/lumx-menu/components/Menu';
import { mdiThumbUpOutline } from '@lumapps/lumx/icons';
import { Theme, FlexBox, Text, ButtonProps } from '@lumapps/lumx/react';
import { ReactionState, ReactionType } from '@lumapps/reactions-core/api/types';
import { useTranslate } from '@lumapps/translations';

import './index.scss';

import { CLASSNAME } from '../../constants';
import { MULTI_REACTIONS } from '../../keys';
import { MultiReactionRadioGroup } from '../MultiReactionRadioGroup';

export interface MultiReactionButtonProps extends Omit<ButtonProps, 'onClick'>, ReactionState {
    /** Ref */
    mergedRefs?: React.RefCallback<HTMLElement>;
    /** Element to focus on close */
    buttonRef?: React.MutableRefObject<HTMLButtonElement | null>;
    /** Callback on emoji selection */
    toggleReaction(reaction: ReactionType): void;
    /** Theme to be used */
    theme?: Theme;
    /** Callback when the emoji reaction picker is toggled */
    onToggle(opening: boolean): void;
    /** Whether the reaction picker is open */
    isOpen?: boolean;
    /** Add custom classname */
    className?: string;
    /** Enable / Disable the component */
    isDisabled?: boolean;
}

const DEBOUNCE_CLOSING_POPOVER_DELAY = 200;

export const MultiReactionButton: React.FC<MultiReactionButtonProps> = ({
    currentUserReactions,
    reactionCounts,
    toggleReaction,
    theme,
    onToggle,
    isOpen,
    buttonRef,
    mergedRefs,
    ...forwardedProps
}) => {
    const { translateAndReplace, pluralize } = useTranslate();
    const { get } = useDataAttributes(CLASSNAME);
    const { element } = useClassnames(CLASSNAME);

    const userReaction = first(currentUserReactions);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debounceClosing = useCallback(
        debounce((setFocus?: boolean) => {
            onToggle(false);

            if (setFocus && buttonRef?.current) {
                buttonRef?.current?.focus();
            }
        }, DEBOUNCE_CLOSING_POPOVER_DELAY),
        [onToggle, buttonRef],
    );

    const onReactionChange = useCallback(
        (reaction: ReactionType) => {
            toggleReaction(reaction);

            debounceClosing(true);
        },
        [debounceClosing, toggleReaction],
    );

    /** Close emoji picker on scroll, to avoid having a floating menu that persists during the page scroll */
    useEffect(() => {
        const closeOnScroll = () => debounceClosing();
        if (isOpen) {
            window.addEventListener('scroll', closeOnScroll);
        }

        return () => {
            window.removeEventListener('scroll', closeOnScroll);
        };
    }, [isOpen, debounceClosing]);

    return (
        <Menu.Trigger
            isOpen={isOpen}
            onToggle={onToggle}
            orientation="horizontal"
            popoverProps={{
                placement: 'top',
                className: element('dialog'),
                fitToAnchorWidth: false,
                isOpen,
            }}
        >
            <Menu.TriggerButton
                aria-label={pluralize(
                    userReaction
                        ? translateAndReplace(MULTI_REACTIONS.CHANGE_REACTION_ARIA_LABEL, {
                              REACTION: userReaction,
                              TOTAL: reactionCounts.total,
                          })
                        : translateAndReplace(MULTI_REACTIONS.REACT_ARIA_LABEL, { TOTAL: reactionCounts.total }),
                    reactionCounts.total || 1,
                )}
                leftIcon={!userReaction ? mdiThumbUpOutline : undefined}
                theme={theme}
                ref={mergedRefs}
                emphasis="low"
                isSelected={!!userReaction}
                {...forwardedProps}
                {...get({ element: 'trigger-button', action: userReaction ? 'update' : 'add' })}
            >
                <FlexBox as="span" orientation="horizontal" vAlign="center" hAlign="center" gap="regular">
                    {userReaction && (
                        <Text as="span" color="dark" className={element('current')}>
                            {userReaction}
                        </Text>
                    )}
                    <Text as="span">{reactionCounts.total}</Text>
                </FlexBox>
            </Menu.TriggerButton>
            <Menu className={classnames(padding('horizontal', 'big'), padding('vertical', 'medium'))}>
                <MultiReactionRadioGroup userReaction={userReaction} onReactionChange={onReactionChange} />
            </Menu>
        </Menu.Trigger>
    );
};
